import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Ticket, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface VoucherCardProps {
  empresa: {
    id: string;
    plano: string;
    voucher_id?: string | null;
    plano_expira_em?: string | null;
  };
  onApplied: () => void;
}

export function VoucherCard({ empresa, onApplied }: VoucherCardProps) {
  const [codigo, setCodigo] = useState('');
  const [loading, setLoading] = useState(false);
  const [voucher, setVoucher] = useState<any>(null);

  const expiraEm = empresa.plano_expira_em ? new Date(empresa.plano_expira_em) : null;
  const expirado = expiraEm && expiraEm.getTime() < Date.now();
  const temVoucherAtivo = empresa.voucher_id && expiraEm && !expirado;

  const handleResgatar = async () => {
    if (!codigo.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('redeem-voucher', {
        body: { codigo: codigo.trim().toUpperCase() },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast({
        title: 'Voucher resgatado!',
        description: `Plano ${(data as any).plano === 'expert' ? 'Expert' : 'Max Pro'} ativado.`,
      });
      setCodigo('');
      onApplied();
    } catch (e: any) {
      toast({
        title: 'Erro ao resgatar voucher',
        description: e?.message || 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ticket className="w-5 h-5" />
          Voucher do Plano
        </CardTitle>
        <CardDescription>
          {temVoucherAtivo
            ? 'Voucher ativo na sua empresa'
            : expirado
              ? 'Seu voucher expirou'
              : 'Tem um voucher? Resgate aqui'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {temVoucherAtivo && expiraEm && (
          <div className="flex items-start gap-2 rounded-lg border border-green-500/30 bg-green-500/10 p-3">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-green-700 dark:text-green-400">
                Plano {empresa.plano === 'expert' ? 'Expert' : 'Max Pro'} ativo
              </p>
              <p className="text-xs text-muted-foreground">
                Expira em {expiraEm.toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        )}

        {expirado && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-destructive">Voucher expirado</p>
              <p className="text-xs text-muted-foreground">
                Expirou em {expiraEm?.toLocaleDateString('pt-BR')}. Resgate um novo voucher para reativar seu plano.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="voucher-code">Código do voucher</Label>
          <div className="flex gap-2">
            <Input
              id="voucher-code"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              placeholder="Ex: BIANCHIM2026"
              className="uppercase"
            />
            <Button onClick={handleResgatar} disabled={loading || !codigo.trim()}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Resgatar'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
